import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  getManageFieldSections,
  useManageFieldsStore,
} from '#/shared/lib/manage-fields'
import type { ManageFieldScope } from '#/shared/lib/manage-fields'
import { cn } from '#/shared/lib/utils'
import { Popover } from '#/shared/ui/popover'

function FieldToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: () => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-5 w-8.5 shrink-0 cursor-pointer items-center rounded-full transition-colors',
        checked ? 'bg-primary' : 'bg-gray-200',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute size-4 rounded-full bg-white shadow-sm transition-all',
          checked ? 'right-0.5' : 'left-0.5',
        )}
      />
    </button>
  )
}

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="px-2.5 pt-1.5 pb-0.5">
      <span className="text-2xs font-medium tracking-wide text-gray-500 uppercase select-none">
        {label}
      </span>
    </div>
  )
}

function ManageFieldsList({ scope }: { scope: ManageFieldScope }) {
  const { t } = useTranslation()
  const hidden = useManageFieldsStore((state) => state.hidden)
  const toggle = useManageFieldsStore((state) => state.toggle)
  const sections = useMemo(() => getManageFieldSections(scope), [scope])
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {sections.map((section) => (
        <div key={section.titleKey} className="flex flex-col">
          <SectionLabel label={t(section.titleKey)} />
          {section.options.map((option) => {
            const label = t(option.labelKey)
            const isVisible = !hidden[option.key]
            return (
              <div
                key={option.key}
                className="flex h-8 items-center justify-between rounded px-2.5"
              >
                <span className="text-sm text-gray-900 select-none">
                  {label}
                </span>
                <FieldToggle
                  checked={isVisible}
                  onChange={() => toggle(option.key)}
                  label={label}
                />
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export function ManageFieldsPopover({ scope }: { scope: ManageFieldScope }) {
  const { t } = useTranslation()
  const hideAll = useManageFieldsStore((state) => state.hideAll)
  const showAll = useManageFieldsStore((state) => state.showAll)
  const allHidden = useManageFieldsStore(
    (state) => Object.keys(state.hidden).length > 0,
  )

  return (
    <Popover
      align="left"
      side="bottom"
      portal
      trigger={(open) => (
        <button
          type="button"
          className={cn(
            'cursor-pointer text-sm underline underline-offset-2 select-none hover:text-primary',
            open ? 'text-primary' : 'text-gray-500',
          )}
        >
          {t('orderCreate.page.customizeFields')}
        </button>
      )}
    >
      <div className="flex max-h-100 w-75 flex-col rounded-md border border-gray-200 bg-white p-1 shadow-popover">
        <div className="flex h-7.5 shrink-0 items-center justify-between border-b border-gray-200 px-1.75 text-xs">
          <span className="text-gray-500 select-none">
            {t('manageFields.title')}
          </span>
          <button
            type="button"
            onClick={allHidden ? showAll : hideAll}
            className="cursor-pointer text-primary select-none"
          >
            {allHidden ? t('manageFields.showAll') : t('manageFields.hideAll')}
          </button>
        </div>
        <ManageFieldsList scope={scope} />
      </div>
    </Popover>
  )
}
